import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { styled } from 'styled-components';

import { getRole, editRole, editRolePermissions } from '../services/RolesService';
import { getPermissions } from '../services/PermissionsService';
import { useAppContext } from '../contextApi/context';
import { ROLE_NAME_DUPLICATED } from '../constants/apiMessages';

const EditRole = () => {
    // ----- Context/Hooks
    const { loggedUser } = useAppContext();
    const { id } = useParams();

    // ----- State
    const [allPermissions, setAllPermissions] = useState([]);
    const [currentRole, setCurrentRole] = useState(null);
    const [newPermissions, setNewPermissions] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [showRoleEdited, setShowRoleEdited] = useState(false);
    const [showPermissionsSaved, setShowPermissionsSaved] = useState(false);
    const [showErrorOnEdit, setShowErrorOnEdit] = useState(false);
    const [showErrorDuplicatedRole, setShowErrorDuplicatedRole] = useState(false);

    // Reference for success message
    const permissionsSavedRef = useCallback(
        (node) => {
            if (node !== null && showPermissionsSaved) {
                node.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        },
        [showPermissionsSaved]
    );

    // ----- API Fetch Actions
    const fetchRoleData = useCallback(async () => {
        return await getRole(loggedUser.token, id);
    }, [loggedUser.token, id]);

    const fetchPermissionsData = useCallback(async () => {
        return await getPermissions(loggedUser.token);
    }, [loggedUser.token]);

    // ----- Hooks
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            name: '',
            description: '',
        },
    });

    useEffect(() => {
        fetchRoleData().then((data) => {
            setCurrentRole(data);
            setNewPermissions(data.permissions.map((role) => role.id));
            const { name, description } = data;
            reset({ name, description });
        });

        fetchPermissionsData().then((data) => {
            const groupedPermissions = Object.groupBy
                ? Object.groupBy(data, ({ entity }) => entity)
                : data.reduce((groups, permission) => {
                      const entity = permission.entity;
                      if (!groups[entity]) groups[entity] = [];
                      groups[entity].push(permission);
                      return groups;
                  }, {});

            const sortedPermissions = Object.keys(groupedPermissions)
                .sort()
                .reduce(
                    (acc, key) => ({
                        ...acc,
                        [key]: groupedPermissions[key],
                    }),
                    {}
                );

            setAllPermissions(sortedPermissions);

            // Expand groups with selected permissions by default
            const groupsToExpand = Object.keys(sortedPermissions).filter((entity) => {
                return sortedPermissions[entity].some((permission) =>
                    data.permissions?.some((rolePermission) => rolePermission.id === permission.id)
                );
            });
            setExpandedGroups(new Set(groupsToExpand));
        });
    }, [fetchPermissionsData, fetchRoleData, reset]);

    // ----- Actions
    const handleEdit = async (data) => {
        clearMessages();
        const response = await editRole(loggedUser.token, currentRole.id, data);

        if (response.status === 200) {
            setShowRoleEdited(true);
        } else {
            // Error from API
            const { data } = response;

            if (data.includes(ROLE_NAME_DUPLICATED)) {
                setShowErrorDuplicatedRole(true);
            }
        }
    };

    const handleChange = (e) => {
        const permissionId = Number(e.target.value);

        if (e.target.checked) {
            // Add permission if checked
            setNewPermissions((prev) => [...prev, permissionId]);
        } else {
            // Remove permission if unchecked
            setNewPermissions((prev) => prev.filter((id) => id !== permissionId));
        }
    };

    const isChecked = (permissionId) => {
        return newPermissions.includes(permissionId);
    };

    // Toggle expand/collapse for a group
    const toggleGroup = (entity) => {
        setExpandedGroups((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(entity)) {
                newSet.delete(entity);
            } else {
                newSet.add(entity);
            }
            return newSet;
        });
    };

    // Select all or none in a group
    const toggleAllInGroup = (entity, permissions, e) => {
        e.stopPropagation();

        const groupPermissionIds = permissions.map((p) => p.id);
        const allSelected = groupPermissionIds.every((id) => newPermissions.includes(id));

        if (allSelected) {
            setNewPermissions((prev) => prev.filter((id) => !groupPermissionIds.includes(id)));
        } else {
            setNewPermissions((prev) => [...new Set([...prev, ...groupPermissionIds])]);
        }
    };

    // Get stats for a group
    const getGroupStats = (permissions) => {
        const total = permissions.length;
        const selected = permissions.filter((p) => newPermissions.includes(p.id)).length;
        return { selected, total };
    };

    // Expand all groups
    const expandAll = () => {
        setExpandedGroups(new Set(Object.keys(filteredPermissions)));
    };

    // Collapse all groups
    const collapseAll = () => {
        setExpandedGroups(new Set());
    };

    const handleSavePermissions = async () => {
        clearMessages();
        const response = await editRolePermissions(loggedUser.token, currentRole.id, newPermissions);

        if (response) {
            setShowPermissionsSaved(true);
        } else {
            // Error from API
            setShowErrorOnEdit(true);
        }
    };

    const clearMessages = () => {
        setShowRoleEdited(false);
        setShowPermissionsSaved(false);
        setShowErrorOnEdit(false);
        setShowErrorDuplicatedRole(false);
    };

    // Filter permissions based on search
    const filteredPermissions = searchTerm
        ? Object.entries(allPermissions).reduce((acc, [entity, permissions]) => {
              const filtered = permissions.filter(
                  (p) =>
                      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      entity.toLowerCase().includes(searchTerm.toLowerCase())
              );
              if (filtered.length > 0) {
                  acc[entity] = filtered;
              }
              return acc;
          }, {})
        : allPermissions;

    // ----- Render
    return (
        <div className='container mt-4'>
            {showRoleEdited && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Rol editado satisfactoriamente.
                </div>
            )}

            {showErrorOnEdit && (
                <div className='alert alert-danger text-center mb-1' role='alert'>
                    Rol no editado.
                </div>
            )}

            {showErrorDuplicatedRole && (
                <div className='alert alert-danger text-center mb-1' role='alert'>
                    El nombre del rol ya se encuentra registrado.
                </div>
            )}

            <div className='mb-3'>
                <Link to='/roles'>Volver a página de roles</Link>
            </div>

            <h1>Editar Rol</h1>
            <FormContainer>
                <form onSubmit={handleSubmit(handleEdit)}>
                    <label className='d-flex mb-1' htmlFor='name'>
                        Nombre
                    </label>
                    <input
                        className='form-control'
                        type='text'
                        id='name'
                        autoComplete='off'
                        maxLength={50}
                        {...register('name', { required: true, maxLength: 50 })}
                        autoFocus
                    />
                    <span className='my-1' role='alert'>
                        {errors.name?.type === 'required' && `Campo requerido`}
                        {errors.name?.type === 'maxLength' && `Formato inválido`}
                    </span>

                    <label className='d-flex mb-1' htmlFor='description'>
                        Descripción
                    </label>
                    <textarea className='form-control' id='description' rows='3' maxLength={100} {...register('description', { maxLength: 100 })} />
                    <span className='my-1' role='alert'>
                        {errors.description?.type === 'maxLength' && `Formato inválido`}
                    </span>
                    <div className='d-flex justify-content-center'>
                        <button className='btn btn-danger mt-3' type='submit'>
                            Guardar cambios
                        </button>
                    </div>
                </form>
            </FormContainer>

            <hr />

            {showPermissionsSaved && (
                <div ref={permissionsSavedRef} className='alert alert-success text-center mb-3' role='alert'>
                    Permisos guardados satisfactoriamente.
                </div>
            )}

            <PermissionsHeader>
                <h1>Gestionar permisos</h1>
                <PermissionsControls>
                    <SearchInput
                        type='text'
                        className='form-control'
                        placeholder='Buscar permisos...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button className='btn btn-outline-secondary btn-sm' onClick={expandAll}>
                        Expandir todos
                    </button>
                    <button className='btn btn-outline-secondary btn-sm' onClick={collapseAll}>
                        Colapsar todos
                    </button>
                </PermissionsControls>
            </PermissionsHeader>

            <PermissionsContainer>
                {Object.entries(filteredPermissions).map(([entity, permissions]) => {
                    if (entity === 'Ordenes de Producción' || entity === 'Órdenes de Trabajo') {
                        return null;
                    }

                    const isExpanded = expandedGroups.has(entity);
                    const stats = getGroupStats(permissions);
                    const allSelected = stats.selected === stats.total;

                    return (
                        <PermissionGroup key={entity}>
                            <GroupHeader onClick={() => toggleGroup(entity)}>
                                <GroupHeaderLeft>
                                    <ExpandIcon expanded={isExpanded}>▶</ExpandIcon>
                                    <GroupTitle>{entity}</GroupTitle>
                                    <Badge selected={allSelected}>
                                        {stats.selected}/{stats.total}
                                    </Badge>
                                </GroupHeaderLeft>
                                <button
                                    type='button'
                                    className={`btn btn-sm ${allSelected ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                                    onClick={(e) => toggleAllInGroup(entity, permissions, e)}
                                >
                                    {allSelected ? 'Desmarcar todos' : 'Marcar todos'}
                                </button>
                            </GroupHeader>

                            {isExpanded && (
                                <PermissionsList>
                                    {permissions.map((permission) => {
                                        return (
                                            <PermissionItem key={permission.id}>
                                                <div className='form-check'>
                                                    <input
                                                        className='form-check-input'
                                                        checked={isChecked(permission.id)}
                                                        type='checkbox'
                                                        value={permission.id}
                                                        onChange={handleChange}
                                                        id={`permission-${permission.id}`}
                                                    />
                                                    <label className='form-check-label' htmlFor={`permission-${permission.id}`}>
                                                        <PermissionName>{permission.name}</PermissionName>
                                                        {permission.description && (
                                                            <PermissionDescription>{permission.description}</PermissionDescription>
                                                        )}
                                                    </label>
                                                </div>
                                            </PermissionItem>
                                        );
                                    })}
                                </PermissionsList>
                            )}
                        </PermissionGroup>
                    );
                })}

                <SaveButtonWrapper>
                    <button className='btn btn-danger' onClick={handleSavePermissions}>
                        Guardar permisos
                    </button>
                </SaveButtonWrapper>
            </PermissionsContainer>
        </div>
    );
};

const FormContainer = styled.div`
    margin: 25px auto 0;
    padding: 40px 40px 20px;
    width: 70%;

    form {
        display: grid;

        > label {
            font-size: 15px;
            font-weight: bold;
        }

        > span {
            color: #d04437;
            font-size: 13px;
            min-height: 12px;
        }

        button {
            background-color: #d04437;
            width: 30%;
        }
    }
`;

const PermissionsHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 15px;

    h1 {
        margin: 0;
    }
`;

const PermissionsControls = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`;

const SearchInput = styled.input`
    width: 250px;
`;

const PermissionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 30px;
`;

const PermissionGroup = styled.div`
    border: 1px solid #dee2e6;
    border-radius: 8px;
    overflow: hidden;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    }
`;

const GroupHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    background: #f8f9fa;
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s;

    &:hover {
        background: #e9ecef;
    }
`;

const GroupHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ExpandIcon = styled.span`
    display: inline-block;
    transition: transform 0.2s;
    font-size: 10px;
    color: #6c757d;
    transform: ${(props) => (props.expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
`;

const GroupTitle = styled.h5`
    margin: 0;
    font-weight: 600;
    color: #495057;
    font-size: 16px;
`;

const Badge = styled.span`
    background: ${(props) => (props.selected ? '#28a745' : '#6c757d')};
    color: white;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
`;

const PermissionsList = styled.div`
    padding: 15px 20px;
    background: white;
`;

const PermissionItem = styled.div`
    margin-bottom: 10px;

    &:last-child {
        margin-bottom: 0;
    }

    .form-check-label {
        cursor: pointer;
        margin-left: 5px;
    }
`;

const PermissionName = styled.div`
    font-weight: 500;
    color: #212529;
`;

const PermissionDescription = styled.div`
    font-size: 13px;
    color: #6c757d;
    margin-top: 2px;
`;

const SaveButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 20px;

    button {
        background-color: #d04437;
        width: 20%;

        &:hover {
            background-color: #b83731;
        }
    }
`;

export default EditRole;
